import React, { FC, useEffect, useState } from 'react';
import { useDebounceHook } from '~/src/hooks/use-debounce.hook';
import { useFetchAutocompleteOrganizations } from '~/src/repositories/organizations/hooks/use-fetch-autocomplete.hook';
import Dropdown from '~/src/components/form-components/dropdown/dropdown';
import {
    getComponentCompanySuggestions,
    prepareDataFromDropDown,
} from '~/src/components/common-components/organization-field/organization-field.utils';
import {
    EMPTY_TEXT,
    ERROR_TEXT,
} from '~/src/components/common-components/organization-field/organization-field.constants';
import { Empty } from '~/src/styles/shared-css/empty-text-error';
import type { CustomInputChangeInterface } from '~/src/components/form-components/dropdown/dropdown.types';
import type { SuggestedOrganizationType } from '~/src/components/blocks/clients-request/organization-info/organization-info.types';
import type { DropDownOrganizationProps } from '~/src/models/types/fields.types';
import type { AutocompleteOrganizationDataInterface } from '~/src/repositories/organizations/types/organizations.types';
import type { DropdownListItemInterface } from '~/src/components/form-components/dropdown/dropdown.types';

export const DropDownOrganizationField: FC<DropDownOrganizationProps> = ({
    enableError = true,
    setDispatchSuggestedOrganization,
    hasNeedClear,
    noReset,
    initialSuggestedOrganization,
    onOrganizationFieldValidationChange,
    ...props
}) => {
    const [organizationArray, setOrganizationArray] = useState<AutocompleteOrganizationDataInterface[] | []>([]);
    const [suggestedOrganization, setSuggestedOrganization] = useState<SuggestedOrganizationType>(
        initialSuggestedOrganization ?? {},
    );
    const [dropdownValue, setDropdownValue] = useState('');
    const [localValue, setLocalValue] = useState(props.value);
    const debouncedDropdownValue = useDebounceHook(dropdownValue);
    const [error, setError] = useState('');
    const { refetch } = useFetchAutocompleteOrganizations(debouncedDropdownValue, setOrganizationArray);
    const EmptyOutputDataComponentArray = [<Empty key={EMPTY_TEXT}>{EMPTY_TEXT}</Empty>];

    useEffect(() => {
        if (onOrganizationFieldValidationChange) {
            const isOrganizationSelected =
                !!suggestedOrganization?.inn && localValue === suggestedOrganization?.name?.fullName;

            const hasValidationError = !isOrganizationSelected && (props.required || !!localValue);

            onOrganizationFieldValidationChange(hasValidationError);
        }
    }, [props.required, localValue, suggestedOrganization?.inn]);

    useEffect(() => {
        !Object.keys(suggestedOrganization).length && debouncedDropdownValue && refetch().then();
    }, [debouncedDropdownValue, suggestedOrganization]);

    useEffect(() => {
        if (!Object.keys(suggestedOrganization).length) {
            return;
        }

        setDispatchSuggestedOrganization(suggestedOrganization);
        setError('');
    }, [suggestedOrganization]);

    useEffect(() => {
        return () => {
            !noReset && setDispatchSuggestedOrganization({});
        };
    }, []);

    useEffect(() => {
        if (hasNeedClear) {
            onClearHandler();
        }
    }, [hasNeedClear]);

    const emptyOutputData: [DropdownListItemInterface] = [
        {
            id: EMPTY_TEXT,
            value: '',
            isEmpty: true,
            isDefaultCursor: true,
            Component: EmptyOutputDataComponentArray,
        },
    ];

    const onBlurHandler = () => {
        if (dropdownValue) {
            setError(Object.keys(suggestedOrganization).length ? '' : ERROR_TEXT);
        }
    };

    const onSuggestedValueChange = (inputData: CustomInputChangeInterface) => {
        const { index } = inputData;
        const suggestedOrganization = organizationArray[index];
        setSuggestedOrganization({ ...suggestedOrganization });
    };

    const onClearHandler = () => {
        setDropdownValue('');
        setError('');
        setSuggestedOrganization({});
        setDispatchSuggestedOrganization({});
    };

    const onValueChangeHandler = (value: string) => {
        setLocalValue(value);
        if (suggestedOrganization?.name?.fullName !== value) {
            setSuggestedOrganization({});
        }

        const hasValue = value !== '';
        const isRequired = props.required;
        if (!hasValue && !isRequired) {
            setError('');
        } else if (hasValue && suggestedOrganization?.name?.fullName !== value) {
            setError(ERROR_TEXT);
        }

        setDropdownValue((value || '').trim());
    };

    return (
        <Dropdown
            onClear={onClearHandler}
            onSuggestedValueChange={onSuggestedValueChange}
            onValueChange={onValueChangeHandler}
            onBlurHandler={onBlurHandler}
            error={enableError || localValue ? error : ''}
            emptyOutputData={emptyOutputData}
            hasNeedClear={hasNeedClear}
            options={prepareDataFromDropDown({
                data: organizationArray,
                callback: getComponentCompanySuggestions,
            })}
            {...props}
        />
    );
};
